// Company360MockData.js

export const entities = ['Company', 'Subsidiary', 'Contact'];

export const mockData = {
  Company: {
    attributes: [
      {
        name: 'CompanyId',
        type: 'String',
        description: 'Unique identifier for each company record.',
        isPK: true,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'CompanyName',
        type: 'String',
        description: 'The official registered name of the company.',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'Industry',
        type: 'String',
        description: 'Type of industry the company operates in.',
        isPK: false,
        isRequired: false,
        isMapped: true,
      },
      {
        name: 'Headquarters',
        type: 'String',
        description: 'Registered location or headquarters address.',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
      {
        name: 'Revenue',
        type: 'Number',
        description: 'Annual revenue reported by the company.',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
    ],

    relationships: [
      { from: 'Company', relationship: 'hasMany', to: 'Subsidiary' },
      { from: 'Company', relationship: 'hasMany', to: 'Contact' },
    ],

    survivorship: [
      {
        id: 1,
        attribute: 'CompanyId',
        strategy: 'Primary Key (Unique Identifier)',
        sourcePriority: '—',
        fallback: 'No Fallback (System Generated)',
      },
      {
        id: 2,
        attribute: 'CompanyName',
        strategy: 'Preferred Source',
        sourcePriority: 'CRM > ERP > External Registry',
        fallback: 'Latest Non-null Value',
      },
      {
        id: 3,
        attribute: 'Industry',
        strategy: 'Most Frequent Value',
        sourcePriority: 'CRM > Data Enrichment > ERP',
        fallback: 'Default = "Unknown"',
      },
      {
        id: 4,
        attribute: 'Headquarters',
        strategy: 'Most Recent Update',
        sourcePriority: 'CRM > ERP',
        fallback: 'Non-null',
      },
      {
        id: 5,
        attribute: 'Revenue',
        strategy: 'Most Recent Update',
        sourcePriority: 'ERP > Financial Reports',
        fallback: 'Previous Known Value',
      },
    ],

    matchRules: [
      {
        label: 'Exact Match by Company Name and Headquarters',
        name: 'Rule1',
        type: 'Potential Match',
        scope: 'All',
      },
      {
        label: 'Fuzzy Match by Company Name and Industry',
        name: 'Rule2',
        type: 'Potential Match',
        scope: 'All',
      },
      {
        label: 'Automatic Match by Registration Number',
        name: 'Rule3',
        type: 'Auto Merge',
        scope: 'All',
      },
    ],

    cleanse: [
      {
        id: 1,
        ruleName: 'Trim Company Name',
        appliesTo: 'CompanyName',
        providerType: 'String Normalization',
        order: 1,
        status: 'Active',
      },
      {
        id: 2,
        ruleName: 'Validate Revenue Format',
        appliesTo: 'Revenue',
        providerType: 'Numeric Validation',
        order: 2,
        status: 'Limited',
      },
      {
        id: 3,
        ruleName: 'Standardize Headquarters Name',
        appliesTo: 'Headquarters',
        providerType: 'String Standardization',
        order: 3,
        status: 'Off',
      },
    ],
  },

  Subsidiary: {
    attributes: [
      {
        name: 'SubsidiaryId',
        type: 'String',
        description: 'Unique identifier for each subsidiary entity.',
        isPK: true,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'CompanyId',
        type: 'String',
        description: 'Foreign key linking subsidiary to parent company.',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'SubsidiaryName',
        type: 'String',
        description: 'Registered name of the subsidiary company.',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'Location',
        type: 'String',
        description: 'Primary operating location of the subsidiary.',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
    ],

    relationships: [
      { from: 'Subsidiary', relationship: 'belongsTo', to: 'Company' },
    ],

    survivorship: [
      {
        id: 1,
        attribute: 'SubsidiaryId',
        strategy: 'Primary Key (Unique Identifier)',
        sourcePriority: '—',
        fallback: 'No Fallback (System Generated)',
      },
      {
        id: 2,
        attribute: 'SubsidiaryName',
        strategy: 'Preferred Source',
        sourcePriority: 'ERP > CRM',
        fallback: 'Latest Non-null Value',
      },
      {
        id: 3,
        attribute: 'Location',
        strategy: 'Most Frequent Value',
        sourcePriority: 'CRM > ERP',
        fallback: 'Non-null',
      },
    ],

    matchRules: [
      {
        label: 'Exact Match by Subsidiary Name and Company ID',
        name: 'RuleA',
        type: 'Potential Match',
        scope: 'All',
      },
      {
        label: 'Fuzzy Match by Location',
        name: 'RuleB',
        type: 'Potential Match',
        scope: 'All',
      },
    ],

    cleanse: [
      {
        id: 1,
        ruleName: 'Capitalize Subsidiary Name',
        appliesTo: 'SubsidiaryName',
        providerType: 'String Normalization',
        order: 1,
        status: 'Active',
      },
      {
        id: 2,
        ruleName: 'Trim Location Field',
        appliesTo: 'Location',
        providerType: 'Whitespace Clean-up',
        order: 2,
        status: 'Limited',
      },
    ],
  },

  Contact: {
    attributes: [
      {
        name: 'ContactId',
        type: 'String',
        description: 'Unique identifier for each contact person.',
        isPK: true,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'CompanyId',
        type: 'String',
        description: 'Foreign key linking the contact to its company.',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'FullName',
        type: 'String',
        description: 'Full name of the contact person.',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'Email',
        type: 'String',
        description: 'Email address of the contact person.',
        isPK: false,
        isRequired: true,
        isMapped: true,
      },
      {
        name: 'Phone',
        type: 'String',
        description: 'Phone number of the contact person.',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
      {
        name: 'Role',
        type: 'String',
        description: 'Designation or role of the contact within the company.',
        isPK: false,
        isRequired: false,
        isMapped: false,
      },
    ],

    relationships: [{ from: 'Contact', relationship: 'belongsTo', to: 'Company' }],

    survivorship: [
      {
        id: 1,
        attribute: 'ContactId',
        strategy: 'Primary Key (Unique Identifier)',
        sourcePriority: '—',
        fallback: 'No Fallback (System Generated)',
      },
      {
        id: 2,
        attribute: 'Email',
        strategy: 'Most Recent Update',
        sourcePriority: 'CRM > HR',
        fallback: 'Previous Valid Email',
      },
      {
        id: 3,
        attribute: 'Phone',
        strategy: 'Preferred Source',
        sourcePriority: 'CRM > HR',
        fallback: 'Latest Non-null Value',
      },
      {
        id: 4,
        attribute: 'FullName',
        strategy: 'Most Frequent Value',
        sourcePriority: 'CRM > HR',
        fallback: 'Non-null',
      },
    ],

    matchRules: [
      {
        label: 'Exact Match by Email Address',
        name: 'RuleX',
        type: 'Auto Merge',
        scope: 'All',
      },
      {
        label: 'Fuzzy Match by Full Name and Company',
        name: 'RuleY',
        type: 'Potential Match',
        scope: 'All',
      },
    ],

    cleanse: [
      {
        id: 1,
        ruleName: 'Normalize Email Format',
        appliesTo: 'Email',
        providerType: 'Email Validation',
        order: 1,
        status: 'Active',
      },
      {
        id: 2,
        ruleName: 'Trim Full Name',
        appliesTo: 'FullName',
        providerType: 'String Normalization',
        order: 2,
        status: 'Active',
      },
      {
        id: 3,
        ruleName: 'Validate Phone Number',
        appliesTo: 'Phone',
        providerType: 'Number Validation',
        order: 3,
        status: 'Limited',
      },
    ],
  },
};
